import type { ApiSchema } from "../apiSchema";
import { jsonBody, requestJson } from "../http/httpClient";
import { JsonContractError, readArray, readInteger, readNullableString, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";
import { threadPath } from "./threadsApi";

/** A problem as the log keeps it: worded by code like every other error (NFR-5). */
export type RecordedProblem = ApiSchema<"RecordedProblem">;

/** One part an agent step's prompt fills itself, as its run would fill it now (F148). */
export interface PromptPart {
  /** The placeholder's name in the prompt. */
  readonly name: string;
  readonly source: "selector" | "input";
  /** The selector kind that fills it, such as `label_list`; `null` for an input. */
  readonly selectorKind: string | null;
  /** The step input an `input` part reads; `null` for a selector. */
  readonly input: string | null;
  /** Its text as the prompt would hold it; `null` while it can't be filled. */
  readonly text: string | null;
  /** The versions it was read from. */
  readonly referencesRead: readonly string[];
  /** Why it can't be filled, as the run would fail; `null` while it only waits. */
  readonly problem: RecordedProblem | null;
}

export interface PromptParts {
  /** The prompt as the step's turn would send it; `null` while a part can't be filled. */
  readonly prompt: string | null;
  readonly parts: readonly PromptPart[];
}

/**
 * What an agent step's prompt fills itself, and the prompt, as module C's preparation would
 * fill them for its run now (F148), with a run's values for its inputs; without any, the
 * Run's own. Writes nothing.
 */
export function readPromptParts(threadId: string, stepKey: string, values: Readonly<Record<string, unknown>>, signal?: AbortSignal): Promise<PromptParts> {
  return requestJson({
    method: "POST",
    path: threadPath(threadId, "/prompt-parts"),
    body: jsonBody({ step_key: stepKey, input_values: values }),
    signal,
    decodeResponse: (body) => {
      const object = readObject(body, "prompt parts");
      return { prompt: readNullableString(object, "prompt"), parts: readArray(object["parts"], "prompt parts", readPromptPart) };
    },
  });
}

function readPromptPart(value: unknown): PromptPart {
  const object = readObject(value, "prompt part");
  const use = readObject(object["use"], "placeholder use");
  return {
    name: readString(use, "placeholder"),
    source: readOneOf(use, "source", ["selector", "input"] as const),
    selectorKind: readNullableString(use, "selector_kind"),
    input: readNullableString(object, "input"),
    text: readNullableString(object, "text"),
    referencesRead: readArray(use["references_read"], "references read", (item) => readString({ item }, "item")),
    problem: object["problem"] === null || object["problem"] === undefined ? null : readProblem(readObject(object["problem"], "problem")),
  };
}

function readProblem(object: JsonObject): RecordedProblem {
  const details = object["details"];
  if (typeof details !== "object" || details === null || Array.isArray(details)) throw new JsonContractError("Expected a problem to have details.");
  return {
    code: readString(object, "code"),
    title: readString(object, "title"),
    status: readInteger(object, "status"),
    detail: readString(object, "detail"),
    details: details as RecordedProblem["details"],
  };
}
